import React, {PropsWithChildren} from "react";
import { withTheme } from "styled-components";
import { OrbitTheme } from "../../../themes/architecture/orbit-theme.interface";
import { SvgSizeUnit } from "../../icon/svg-icon/svg.icon";
import { IButtonCommonProps, ButtonPreset, ButtonStroke, ButtonSizeVariant, IButtonIconProps, IButtonColorSet } from "./button-props.interface";
import { ButtonBox } from "./boxes/button.box";
import { ButtonLabelBox } from "./boxes/label/button-label.box";
import { ButtonIconWrapperBox } from "./boxes/button-icon-wrapper.box";
import { FlexShrinkItemBox } from "../../../themes/architecture/css-helpers/flex";
import { ButtonLabelPaddingBox } from "./boxes/label-padding-box/button-label-padding.box";
import { computeColorSet } from "./button-hover-colors-helpers";
import {ButtonSkeletonBox} from "../skeleton/button-skeleton.box";

interface IButtonProps extends IButtonCommonProps {
    preset: ButtonPreset;
    stroke?: ButtonStroke;
    icon?: IButtonIconProps;
    sizeVariant?: ButtonSizeVariant;
    className?: string;
    tooltip?: string;

    /**
     * theme is addded by styled-component's withTheme
     */
    theme: OrbitTheme;

    /* button interactions */
    onClick?(): void;
    onMouseEnter?(): void;
    onMouseLeave?(): void;

    /** skeleton */
    skeleton?: boolean;
}

const ButtonIcon: React.FC<{IconComponent: IButtonIconProps["Component"], size: number, options?: {reverseOnRTL?: boolean}}> = (props) => {
    if(!props.IconComponent) {
        return null;
    }

    const IconComponent = props.IconComponent;
    return <ButtonIconWrapperBox reverseOnRTL={props.options?.reverseOnRTL}>
        <IconComponent sizeUnit={SvgSizeUnit.Em} size={props.size} />
    </ButtonIconWrapperBox>
};

const $Button: React.FC<PropsWithChildren<IButtonProps>> = (props) => {

    let {
        children, theme, preset, icon, stroke, sizeVariant, rounded, elevated, tooltip, labelOverflow,
        onClick, onMouseEnter, onMouseLeave,
        ...otherProps
    } = props;
    let hasIcon = !!icon;
    let hasLabel = !!children;

    onClick = props.disabled ? undefined : onClick;
    onMouseEnter = props.disabled ? undefined : onMouseEnter;
    onMouseLeave = props.disabled ? undefined : onMouseLeave;

    let presetValues = theme.baseUIComponents.button.presets[preset];
    let themePresetDefaults = theme.baseUIComponents.button.defaults;

    const colorSet: IButtonColorSet = computeColorSet(presetValues.colors, theme.baseUIComponents.button.disabledColorSet);

    stroke = stroke ?? presetValues.stroke ?? themePresetDefaults.stroke;
    rounded = rounded ?? presetValues.rounded ?? themePresetDefaults.rounded;
    elevated = elevated ?? presetValues.elevated ?? themePresetDefaults.elevated;
    sizeVariant = sizeVariant || "s";
    labelOverflow = labelOverflow || "expand";

    let iconSize = icon && icon.size ? icon.size : "regular";
    let iconPlacement = icon && icon.placement ? icon.placement : "left";

    let strokeWidth = theme.baseUIComponents.button.strokeValues[stroke];
    let width = theme.baseUIComponents.button.sizeVariantValues[sizeVariant].width;
    let height = theme.baseUIComponents.button.sizeVariantValues[sizeVariant].height;
    let iconSizeValue = theme.baseUIComponents.button.sizeVariantValues[sizeVariant].iconSize[iconSize];
    let fontSize = theme.baseUIComponents.button.sizeVariantValues[sizeVariant].fontSize || presetValues.fontSize || themePresetDefaults.fontSize;
    let lateralPadding = sizeVariant === "xs" ? 0.6 : 1;

    if (props.skeleton) {
        return <ButtonSkeletonBox
            width={width}
            height={height}
            rounded={rounded}
            margin={props.margin}
            fullWidth={props.fullWidth}
            strokeWidth={strokeWidth}
            iconOnly={hasIcon && !hasLabel}
            noRemUnits={props.noRemUnits}
        />
    }

    return <ButtonBox
        colorSet={colorSet}
        hasLabel={hasLabel}
        hasIcon={hasIcon}
        strokeWidth={strokeWidth}
        width={width}
        height={height}
        iconSize={iconSizeValue}
        lateralPadding={lateralPadding}
        labelOverflow={labelOverflow}
        rounded={rounded}
        elevated={elevated}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        title={tooltip}
        {...otherProps}
    >
        { icon ? (iconPlacement === "left" ? <ButtonIcon
            IconComponent={icon.Component}
            size={iconSizeValue}
            options={{reverseOnRTL: icon.reverseOnRTL}}
        /> : <FlexShrinkItemBox flexShrink={100} flexBasis={iconSizeValue + "em"} style={{height: iconSizeValue + "em"}} />) : null }
        { props.children ? <ButtonLabelPaddingBox
            lateralPadding={lateralPadding}
            sizeVariant={sizeVariant}
            labelOverflow={labelOverflow}
        >
            <ButtonLabelBox
                fontSize={fontSize}
            >
                {props.children}
            </ButtonLabelBox>
        </ButtonLabelPaddingBox> : null }
        { icon ? (iconPlacement === "right" ? <ButtonIcon
            IconComponent={icon.Component}
            size={iconSizeValue}
            options={{reverseOnRTL: icon.reverseOnRTL}}
        /> : <FlexShrinkItemBox flexShrink={100} flexBasis={iconSizeValue + "em"} style={{height: iconSizeValue + "em"}} />) : null }
    </ButtonBox>;

}

export const Button = withTheme($Button);
